/*
 * INTEX CUSTOM — not part of vanilla Ignite. See INTEX-CHANGES.md.
 *
 * Drives both "Find Parts" CTA placements (desktop pill, drawer row) from the
 * compatibility finder's own saved entries, so the two stay in sync. Re-renders
 * on `compatibility-finder:change` (same tab) and `storage` (other tabs) — the
 * same two signals assets/compatibility-badge.js listens to.
 *
 * Talks to Benchmark's element only through its public methods, open() and
 * openForm(entry), so no edit to compatibility-finder.js was needed for this.
 *
 * Copy is verbatim from the design handoff (UK English).
 */
(() => {
  const STORAGE_KEY = 'theme-compatibility-finder';
  const CHANGE_EVENT = 'compatibility-finder:change';
  const FLAG = '__intexFinderCtaBound';

  if (window[FLAG]) return;
  window[FLAG] = true;

  /* Defaults. `emptyTitle`, `emptySub` and `multiSub` are editable per shop —
     snippets/intex-finder-cta.liquid publishes the block's values as
     window.intexFinderCtaCopy from the pill render, which is the one with block
     scope. These must stay in step with the Liquid fallbacks in that snippet. */
  const COPY = {
    emptyTitle: 'INTEX PARTS FINDER',
    emptySub: 'Select your Intex model',
    oneEyebrow: 'Parts for',
    multiSub: 'Choose one to see parts',
    multiTitle: count => `${count} saved models`
  };

  const normalise = value => (value == null ? '' : String(value)).trim();

  /* Read afresh on every paint rather than merged once at load, so an editor
     edit to any of the three settings lands on the next render. Only non-blank
     strings are taken, so clearing a setting falls back to the default above
     instead of emptying the line. */
  function copy() {
    const published = window.intexFinderCtaCopy;
    if (!published || typeof published !== 'object') return COPY;

    const merged = { ...COPY };
    ['emptyTitle', 'emptySub', 'multiSub'].forEach(key => {
      const value = normalise(published[key]);
      if (value) merged[key] = value;
    });
    return merged;
  }

  /* Sentence case for the accessible name — the visible heading is uppercase to
     match the nav links, but screen readers should not get shouted at. Applied
     only to text that is actually all-caps, so an editor who types
     "Intex parts finder" gets it back unchanged. */
  function speakable(text) {
    const value = normalise(text);
    if (!value || value !== value.toUpperCase() || !/\p{Lu}{2}/u.test(value)) return value;
    return value.charAt(0) + value.slice(1).toLowerCase();
  }

  function readEntries() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(entry => entry && entry.id && Array.isArray(entry.filters) && entry.filters.length);
    } catch (error) {
      return [];
    }
  }

  function finder() {
    return document.querySelector('compatibility-finder');
  }

  /* Mirrors compatibility-finder.js buildUrl(): the saved filters, then the
     extra params our picker attached (model_title).

     The base follows the same order as compatibility-finder.js activate()
     (INTEX-CHANGES.md §14.32): the picker's CURRENT data-results-url first,
     the saved entry.url second, Benchmark's data-all-url last. entry.url is
     written into localStorage at the moment the model is saved, so preferring
     it — which this function used to do — pinned the chip's destination to
     whatever the block setting said back then, for good. A shopper who saved a
     model before the results collection changed kept landing on the old one.

     `quickFilter` only exists once compatibility-finder.js has upgraded the
     element, and that script is lazy-loaded, so the picker's own attribute is
     read directly as the second choice — same setting, no wait. */
  function resultsUrl(entry) {
    const element = finder();
    const base = element?.quickFilter?.dataset?.resultsUrl
      || document.querySelector('intex-compat-picker')?.dataset.resultsUrl
      || entry.url
      || element?.dataset.allUrl
      || '/collections/all-parts';
    const url = new URL(base, window.location.origin);

    entry.filters.forEach(filter => {
      if (filter?.param) url.searchParams.set(filter.param, filter.value);
    });

    if (entry.params) {
      Object.entries(entry.params).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
      });
    }

    return url.toString();
  }

  /* Shared with assets/intex-fitment-checker.js (the product page's fit
     checker), so both read the saved models and build the results URL one
     way. A second load of this file from that snippet stops at the FLAG guard
     above, so this is set once, by whichever copy runs first. */
  window.intexFinder = { readEntries, resultsUrl, finder };

  function show(element, isVisible) {
    if (element) element.hidden = !isVisible;
  }

  function setText(element, text) {
    if (element) element.textContent = text;
  }

  function renderRoot(root, entries) {
    const text = copy();
    const state = entries.length === 0 ? 'empty' : entries.length === 1 ? 'one' : 'multi';
    const entry = entries.length === 1 ? entries[0] : null;

    root.dataset.state = state;

    const disc = root.querySelector('[data-intex-cta-disc]');
    const thumb = root.querySelector('[data-intex-cta-thumb]');
    const badge = root.querySelector('[data-intex-cta-badge]');
    const eyebrow = root.querySelector('[data-intex-cta-eyebrow]');
    const title = root.querySelector('[data-intex-cta-title]');
    const sub = root.querySelector('[data-intex-cta-sub]');
    const chevron = root.querySelector('[data-intex-cta-chevron]');
    const edit = root.querySelector('[data-intex-cta-edit]');
    const main = root.querySelector('[data-intex-cta-main]');

    /* Left visual: disc for empty and multi, model thumbnail for one. The
       thumbnail only replaces the disc when we actually have an image. */
    const image = entry && normalise(entry.image);
    const useThumb = state === 'one' && Boolean(image);
    if (useThumb) thumb.src = image;
    show(thumb, useThumb);
    show(disc, !useThumb);

    // Count badge on multi only.
    if (badge) {
      setText(badge, String(entries.length));
      show(badge, state === 'multi');
    }

    if (state === 'empty') {
      show(eyebrow, false);
      setText(title, text.emptyTitle);
      setText(sub, text.emptySub);
      show(sub, true);
      show(chevron, true);
      show(edit, false);
    } else if (state === 'one') {
      const name = normalise(entry.name) || text.emptyTitle;
      show(eyebrow, true);
      setText(eyebrow, text.oneEyebrow);
      setText(title, name);
      show(sub, false);
      show(chevron, false);
      show(edit, true);
    } else {
      show(eyebrow, false);
      setText(title, text.multiTitle(entries.length));
      setText(sub, text.multiSub);
      show(sub, true);
      show(chevron, true);
      show(edit, false);
    }

    /* Accessible name. The pill's main area is a <span> inside Benchmark's
       <summary>, so the summary carries the label there. */
    const accessibleName =
      state === 'one'
        ? `${text.oneEyebrow} ${normalise(entry.name)}. Shop parts that fit.`
        : state === 'multi'
          ? `${text.multiTitle(entries.length)}. ${text.multiSub}.`
          : `${speakable(text.emptyTitle)}. ${text.emptySub}.`;

    const labelTarget = root.dataset.placement === 'pill' ? root.closest('summary') : main;
    labelTarget?.setAttribute('aria-label', accessibleName);

    root.__intexEntry = entry;
  }

  function renderAll() {
    const entries = readEntries();
    document.querySelectorAll('[data-intex-cta]').forEach(root => renderRoot(root, entries));
  }

  /* --------------------------------------------------------------- ACTIONS */

  /* Opened from the drawer row, the finder panel would otherwise appear behind
     the open burger drawer. Collapse the drawer's <details> first. */
  function closeEnclosingDrawer(root) {
    if (root.dataset.placement !== 'drawer') return;

    const drawer = root.closest('details');
    if (!drawer) return;

    drawer.removeAttribute('open');
    drawer.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  }

  function openPanel() {
    const element = finder();
    if (!element) return false;

    if (typeof element.open === 'function') {
      element.open();
      return true;
    }

    // Fall back to toggling the <details> directly.
    element.querySelector('details')?.setAttribute('open', '');
    return true;
  }

  /* Opens the finder with this entry preselected, i.e. straight at the model
     step. openForm() is a public method on Benchmark's element. */
  function openEdit(entry) {
    const element = finder();
    if (!element) return;

    openPanel();

    if (entry && typeof element.openForm === 'function') {
      element.openForm(entry);
    }
  }

  function onMainActivate(root, event) {
    const entry = root.__intexEntry;

    /* One saved model: the main area goes straight to its filtered results.
       Empty and multi open the panel (multi lands on the saved list, which is
       the panel's default view). */
    if (root.dataset.state === 'one' && entry) {
      event.preventDefault();
      event.stopPropagation();
      window.location.href = resultsUrl(entry);
      return;
    }

    if (root.dataset.placement === 'pill') return; // let <summary> toggle

    event.preventDefault();

    /* The drawer row is rendered from header-drawer.liquid, so it is not a
       descendant of <compatibility-finder>. Benchmark's onDocumentClick
       (compatibility-finder.js) closes the panel for any click it sees that is
       outside the element — so without stopping propagation here, the panel
       opened and then immediately closed again inside the same click. This is
       why the pill worked and the drawer row did not. */
    event.stopPropagation();

    closeEnclosingDrawer(root);
    openPanel();
  }

  function bind(root) {
    if (root.__intexBound) return;
    root.__intexBound = true;

    const main = root.querySelector('[data-intex-cta-main]');
    const edit = root.querySelector('[data-intex-cta-edit]');

    if (root.dataset.placement === 'pill') {
      /* Capture phase on the <summary>, so this runs before Benchmark's own
         bubble-phase onSummaryClick and can redirect instead of toggling. */
      root.closest('summary')?.addEventListener('click', event => {
        if (event.target.closest('[data-intex-cta-edit]')) return;
        onMainActivate(root, event);
      }, true);
    } else {
      main?.addEventListener('click', event => onMainActivate(root, event));
    }

    edit?.addEventListener('click', event => {
      // Both are required inside a <summary>: stopPropagation keeps Benchmark's
      // handler out, preventDefault stops the native <details> toggle.
      event.preventDefault();
      event.stopPropagation();
      closeEnclosingDrawer(root);
      openEdit(root.__intexEntry);
    });
  }

  function start() {
    document.querySelectorAll('[data-intex-cta]').forEach(bind);
    renderAll();
  }

  document.addEventListener(CHANGE_EVENT, renderAll);

  /* The theme editor replaces the whole header section on every setting change,
     so the pill and drawer row come back as fresh, unbound DOM carrying the new
     copy. start() is idempotent — bind() no-ops on a root it already owns. */
  document.addEventListener('shopify:section:load', start);
  window.addEventListener('storage', event => {
    if (event.key === null || event.key === STORAGE_KEY) renderAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();